/* Section page building blocks: header with stats, tabs, lesson list, level and challenge cards. */
import type { ReactNode } from 'react';
import { useGo } from '../../app/navigation';
import { lessonsOf } from '../../content/lessons';
import { isLessonDone, levelState, type ModuleId, useProgress } from '../../core/store';
import { Icon, type IconName } from '../../ui/icons';
import { Rich } from '../../ui/Rich';
import { modStyle } from '../../ui/style';
import { Stars, Tabs } from '../../ui/widgets';
import { MODULE_INFO } from './info';

/** Anything shown as a card in a level grid. */
export interface LevelCardItem {
  key: string;
  route: string;
  title: string;
  desc?: string;
  /** "Seviye n" label; challenges show `tag` instead */
  n?: number;
  tag?: string;
  badge?: string;
  challenge?: boolean;
}

export function moduleStats(module: string, levels: readonly { key: string }[] = []) {
  const lessons = lessonsOf(module);
  const done = lessons.filter((l) => isLessonDone(module, l.id)).length;
  let stars = 0;
  for (const lv of levels) stars += levelState(lv.key)?.stars || 0;
  return { done, total: lessons.length, stars, starsTotal: levels.length * 3 };
}

/** Next recommended lesson and level of a module. */
export function nextStepOf<L extends { key: string }>(module: string, levels: readonly L[] = []) {
  const lessons = lessonsOf(module);
  const lesson = lessons.find((x) => !isLessonDone(module, x.id));
  const level = levels.find((x) => !((levelState(x.key)?.stars ?? 0) >= 2));
  return { lesson, level };
}

export interface TabDef<T extends string> {
  id: T;
  label: string;
  icon: IconName;
}

export function ModulePage<T extends string>({
  id,
  levels,
  tabs,
  active,
  children,
}: {
  id: ModuleId;
  levels?: readonly { key: string }[];
  tabs: readonly TabDef<T>[];
  active: T;
  children: ReactNode;
}) {
  const go = useGo();
  useProgress();
  const inf = MODULE_INFO[id];
  const st = moduleStats(id, levels);
  return (
    <div className="mod-page" style={modStyle(id)}>
      <header className="mod-head">
        <div className="mod-icon">
          <Icon name={inf.icon} />
        </div>
        <div className="mod-titles">
          <h1>{inf.title}</h1>
          <p>{inf.lead}</p>
        </div>
        <div className="mod-meta">
          <div className="mm-item">
            <span className="mm-v">
              {st.done}/{st.total}
            </span>
            <span className="mm-k">ders</span>
          </div>
          {levels ? (
            <div className="mm-item">
              <span className="mm-v">
                {st.stars}/{st.starsTotal}
              </span>
              <span className="mm-k">yıldız</span>
            </div>
          ) : null}
        </div>
      </header>
      <Tabs items={tabs} active={active} onChange={(t) => go(t === tabs[0]?.id ? id : `${id}.${t}`)} />
      <div className="mod-body">{children}</div>
    </div>
  );
}

export function GroupTitle({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="group-head">
      <h2>{title}</h2>
      {sub ? (
        <p>
          <Rich text={sub} />
        </p>
      ) : null}
    </div>
  );
}

export function LessonList({ module }: { module: string }) {
  const go = useGo();
  useProgress();
  const lessons = lessonsOf(module);
  const nextIdx = lessons.findIndex((l) => !isLessonDone(module, l.id));
  return (
    <ol className="lesson-list-nav">
      {lessons.map((l, i) => {
        const done = isLessonDone(module, l.id);
        return (
          <li key={l.id}>
            <button
              type="button"
              className={`lesson-item${done ? ' done' : ''}${i === nextIdx ? ' next' : ''}`}
              onClick={() => go(`${module}.ders.${l.id}`)}
            >
              <span className="li-num">{done ? <Icon name="check" /> : String(i + 1)}</span>
              <span className="li-text">
                <span className="li-title">
                  <Rich text={l.title} />
                </span>
                <span className="li-sum">
                  <Rich text={l.summary || ''} />
                </span>
              </span>
              <span className="li-meta">{l.minutes || 5} dk</span>
              <Icon name="chev" />
            </button>
          </li>
        );
      })}
    </ol>
  );
}

export function LevelGrid({ items }: { items: readonly LevelCardItem[] }) {
  const go = useGo();
  useProgress();
  return (
    <div className="level-grid">
      {items.map((it) => {
        const s = levelState(it.key);
        return (
          <button key={it.key} type="button" className="level-card" onClick={() => go(it.route)}>
            <div className="lc-top">
              <span className="lc-n">{it.n != null ? `Seviye ${it.n}` : it.tag || ''}</span>
              <Stars n={s ? s.stars : 0} />
            </div>
            <div className="lc-title">{it.title}</div>
            <div className="lc-desc">
              <Rich text={it.desc || ''} />
            </div>
            <div className="lc-foot">
              {it.badge ? <span className="lc-badge">{it.badge}</span> : null}
              {s ? (
                <span className="lc-best">
                  {it.challenge ? `Rekor: ${s.best || 0}` : `En iyi: %${Math.round((s.acc || 0) * 100)}`}
                </span>
              ) : (
                <span className="lc-best dim">Henüz denenmedi</span>
              )}
              <span className="lc-go">
                <Icon name="right" />
              </span>
            </div>
          </button>
        );
      })}
    </div>
  );
}
